import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BrokerContext, type IBrokerContextValue } from '../../hooks/useBroker/useBroker';
import { useUser } from '../../hooks/useUser/useUser';
import type { ICommercialAddonsResponse, ICommercialPlanBundle } from '../../interfaces/quotes';
import Quotes from '../../services/api/quotes/quotes';
import type { AddonTypeById } from '../../types/quotes';

interface IBrokerProviderProps {
  children: React.ReactNode;
}

const EMPTY_COMMERCIAL_PLANS: ICommercialPlanBundle[] = [];

const BrokerProvider = ({ children }: IBrokerProviderProps) => {
  const { brokerId, loading: userLoading } = useUser();

  const [commercialAddons, setCommercialAddons] = useState<ICommercialAddonsResponse>([]);
  const [loadingAddons, setLoadingAddons] = useState(true);
  const [commercialPlans, setCommercialPlans] = useState<ICommercialPlanBundle[]>(EMPTY_COMMERCIAL_PLANS);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const fetchIdRef = useRef(0);
  const fetchPlansIdRef = useRef(0);

  const fetchCommercialAddons = useCallback(async () => {
    if (userLoading) return;
    if (!brokerId) {
      setCommercialAddons([]);
      setLoadingAddons(false);
      return;
    }
    const id = ++fetchIdRef.current;
    try {
      setLoadingAddons(true);
      const data = await Quotes.getCommercialAddons(brokerId);
      if (id === fetchIdRef.current) setCommercialAddons(data);
    } catch {
      if (id === fetchIdRef.current) setCommercialAddons([]);
    } finally {
      if (id === fetchIdRef.current) setLoadingAddons(false);
    }
  }, [brokerId, userLoading]);

  const fetchCommercialPlans = useCallback(async () => {
    if (userLoading) return;
    if (!brokerId) {
      setCommercialPlans(EMPTY_COMMERCIAL_PLANS);
      setLoadingPlans(false);
      return;
    }
    const id = ++fetchPlansIdRef.current;
    try {
      setLoadingPlans(true);
      const data = await Quotes.getCommercialPlans(brokerId);
      if (id === fetchPlansIdRef.current) setCommercialPlans(data);
    } catch {
      if (id === fetchPlansIdRef.current) setCommercialPlans(EMPTY_COMMERCIAL_PLANS);
    } finally {
      if (id === fetchPlansIdRef.current) setLoadingPlans(false);
    }
  }, [brokerId, userLoading]);

  useEffect(() => {
    fetchCommercialAddons();
  }, [fetchCommercialAddons]);

  useEffect(() => {
    fetchCommercialPlans();
  }, [fetchCommercialPlans]);

  const addonTypeById = useMemo<AddonTypeById>(() => {
    const map: AddonTypeById = new Map();
    for (const typeGroup of commercialAddons) {
      for (const entry of typeGroup.groups) {
        for (const addon of entry.addons) {
          map.set(addon.id, typeGroup.type);
        }
      }
    }
    return map;
  }, [commercialAddons]);

  const getAddonType = useCallback((addonId: number) => addonTypeById.get(addonId), [addonTypeById]);

  const value = useMemo<IBrokerContextValue>(
    () => ({ commercialAddons, addonTypeById, getAddonType, loadingAddons, commercialPlans, loadingPlans }),
    [commercialAddons, addonTypeById, getAddonType, loadingAddons, commercialPlans, loadingPlans]
  );

  return <BrokerContext.Provider value={value}>{children}</BrokerContext.Provider>;
};

export default BrokerProvider;
